/**
 * Top bar: menu (phones), organisation, the global search box (press / anywhere),
 * upload, processing queue, notifications, theme, user menu. On a cabinet or folder
 * page the search carries a removable scope chip (FR-SRC-07).
 */
import { Bell, Building2, Check, CheckCheck, KeyRound, Layers, LogOut, Menu, Monitor, Moon, Search, Sun, Upload, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { moduleOn } from '../../config/features';
import { DateText, IconButton, MenuItem, Popover, SEARCH_INPUT_ID, SearchBox, StatusChip, cx, useToast } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { count } from '../../lib/format';
import { P } from '../../lib/permissions';
import { useCabinets, useNotifications, useQueueSummary } from '../../lib/queries';
import { useTheme } from '../../lib/theme';

export function TopBar({ onMenu }: { onMenu: () => void }) {
  const { can, user } = useAuth();
  const navigate = useNavigate();
  // The search page has its own big box; a second one here would be redundant.
  const onSearchPage = useLocation().pathname === '/search';
  // N9 cleanup: search, upload and the processing queue belong to the hidden QXM library
  const library = moduleOn('library');
  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-1 border-b border-line bg-card/95 px-2 backdrop-blur sm:gap-2 sm:px-4">
      <IconButton label="Open menu" onClick={onMenu} className="lg:hidden">
        <Menu aria-hidden className="h-5 w-5" />
      </IconButton>
      <OrgSwitcher />
      {user?.org && can(P.docsView) && library && !onSearchPage && (
        <>
          <GlobalSearch />
          <IconButton label="Search" className="md:hidden" onClick={() => navigate('/search')}>
            <Search aria-hidden className="h-5 w-5" />
          </IconButton>
        </>
      )}
      <div className="ml-auto flex items-center gap-0.5">
        {user?.org && can(P.docsUpload) && library && (
          <Link to="/upload" className="mr-1 hidden min-h-touch items-center gap-2 rounded-tag bg-brand px-3.5 text-sm font-semibold text-brand-ink hover:bg-brand/90 sm:inline-flex" aria-label="Upload documents">
            <Upload aria-hidden className="h-4 w-4" />
            <span className="hidden lg:inline">Upload</span>
          </Link>
        )}
        {user?.org && library && (can(P.docsUpload) || can(P.inboxReview)) && <QueueStatus />}
        <NotificationsBell />
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}

function OrgSwitcher() {
  const { user, switchOrg } = useAuth();
  const toast = useToast();
  if (!user) return null;
  const name = user.org?.name ?? (user.platformAdmin ? 'ITCSI platform' : 'No organisation');
  const label = (
    <>
      <Building2 aria-hidden className="h-4 w-4 shrink-0 text-muted" />
      <span className="truncate">{name}</span>
    </>
  );
  // Platform admins switch between the platform and organisations with a live support session.
  if (user.platformAdmin ? user.orgs.length === 0 : user.orgs.length <= 1) return <span className="hidden min-h-touch min-w-0 max-w-[220px] items-center gap-2 px-2 text-sm font-semibold sm:inline-flex">{label}</span>;
  return (
    <Popover
      label="Organisation"
      trigger={({ open, toggle, id }) => (
        <button type="button" aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={toggle} className="inline-flex min-h-touch min-w-0 max-w-[42vw] items-center gap-2 rounded-tag border border-line px-3 text-sm font-semibold hover:bg-sunken sm:max-w-[240px]">
          {label}
        </button>
      )}
    >
      {(close) => (
        <div className="max-h-[60vh] w-72 overflow-auto">
          <p className="px-2.5 pb-1 pt-1.5 text-xs text-muted">Switch organisation. Each one’s documents stay separate.</p>
          {user.orgs.map((o) => (
            <MenuItem
              key={o.id}
              icon={user.org?.id === o.id ? <Check className="text-brand" /> : <span className="block h-4 w-4" />}
              onClick={() => {
                close();
                if (o.id !== user.org?.id) void switchOrg(o.id).catch(() => toast.error('Could not switch organisation. Try again.'));
              }}
            >
              {o.name}
            </MenuItem>
          ))}
          {user.platformAdmin && (
            <MenuItem
              icon={!user.org ? <Check className="text-brand" /> : <span className="block h-4 w-4" />}
              onClick={() => {
                close();
                if (user.org) void switchOrg(null).catch(() => toast.error('Could not switch back to the platform. Try again.'));
              }}
            >
              ITCSI platform
            </MenuItem>
          )}
        </div>
      )}
    </Popover>
  );
}

function GlobalSearch() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const cabinets = useCabinets();
  const onSearchPage = pathname === '/search';
  const [q, setQ] = useState(() => (onSearchPage ? new URLSearchParams(search).get('q') ?? '' : ''));
  // Scope: the cabinet page you are on (folder scope is added by D4 with the folder name).
  const m = /^\/cabinets\/([^/]+)(?:\/f\/([^/]+))?/.exec(pathname);
  const cabinet = m ? cabinets.data?.find((c) => c.id === decodeURIComponent(m[1])) : undefined;
  const [scoped, setScoped] = useState(true);
  useEffect(() => setScoped(true), [pathname]);
  useEffect(() => {
    if (onSearchPage) setQ(new URLSearchParams(search).get('q') ?? '');
  }, [onSearchPage, search]);
  const scope = cabinet && scoped ? { label: cabinet.name, onClear: () => setScoped(false) } : null;
  return (
    <SearchBox
      id={SEARCH_INPUT_ID}
      value={q}
      onChange={setQ}
      scope={scope}
      shortcutHint
      className="hidden flex-1 md:block md:max-w-2xl"
      onSubmit={(term) => {
        const p = new URLSearchParams({ q: term });
        if (scope && m) {
          p.set('cabinet', decodeURIComponent(m[1]));
          if (m[2]) p.set('folder', decodeURIComponent(m[2]));
        }
        navigate(`/search?${p.toString()}`);
      }}
    />
  );
}

function QueueStatus() {
  const queue = useQueueSummary();
  const d = queue.data;
  const busy = d ? d.queued + d.running : 0;
  const label = !d ? 'Processing queue' : d.failed ? `${d.failed} failed to process` : busy ? `${busy} being processed` : 'Nothing being processed';
  return (
    <Popover
      align="right"
      label="Processing"
      trigger={({ open, toggle, id }) => (
        <button type="button" aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={toggle} aria-label={label} title={label} className={cx('relative inline-flex min-h-touch items-center gap-1.5 rounded-tag px-2.5 text-sm font-semibold hover:bg-sunken', d?.failed ? 'text-danger' : busy ? 'text-violet' : 'text-muted')}>
          <Layers aria-hidden className={cx('h-5 w-5', busy > 0 && 'motion-safe:animate-pulse')} />
          {(busy > 0 || !!d?.failed) && <span className="tabular">{count(d?.failed || busy)}</span>}
        </button>
      )}
    >
      {(close) => (
        <div className="w-80 p-2">
          <p className="text-sm font-semibold">{label}</p>
          <p className="mt-0.5 text-xs text-muted">New files are checked for viruses, read (OCR) and indexed. They become searchable when done.</p>
          {queue.isError && <p className="mt-2 text-sm text-muted">The processing queue is not available yet.</p>}
          {d?.items && d.items.length > 0 && (
            <ul className="ledger mt-2 max-h-64 overflow-auto rounded-tag border border-line text-sm">
              {d.items.map((it) => (
                <li key={it.id} className="flex items-center gap-2 px-2.5 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{it.title}</span>
                    {it.stage && <span className="block truncate text-xs text-muted">{it.stage}</span>}
                  </span>
                  <StatusChip value={it.status} compact />
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {!!d?.needsReview && (
              <Link to="/documents/inbox" onClick={close} className="font-semibold text-brand underline-offset-2 hover:underline">
                {count(d.needsReview)} waiting in the inbox
              </Link>
            )}
            <Link to="/processing" onClick={close} className="font-semibold text-brand underline-offset-2 hover:underline">
              See all processing
            </Link>
          </div>
        </div>
      )}
    </Popover>
  );
}

function NotificationsBell() {
  const n = useNotifications();
  const list = n.data ?? [];
  const unread = list.filter((x) => !x.read).length;
  return (
    <Popover
      align="right"
      label="Notifications"
      trigger={({ open, toggle, id }) => (
        <IconButton label={unread ? `${unread} unread notifications` : 'Notifications'} aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={toggle} badge={unread}>
          <Bell aria-hidden className="h-5 w-5" />
        </IconButton>
      )}
    >
      {(close) => (
        <div className="w-80">
          <p className="px-2.5 pb-1 pt-2 text-sm font-semibold">Notifications</p>
          {list.length === 0 ? (
            <p className="px-2.5 pb-3 text-sm text-muted">{n.isError ? 'Notifications are not available yet.' : 'Nothing new. Batches, approvals and alerts show up here.'}</p>
          ) : (
            <ul className="ledger max-h-80 overflow-auto">
              {list.map((a) => (
                <li key={a.id}>
                  <Link to={a.href ?? '/notifications'} onClick={close} className="flex gap-2.5 px-2.5 py-2.5 hover:bg-sunken">
                    <span aria-hidden className={cx('mt-1.5 h-2 w-2 shrink-0 rounded-full', a.read ? 'bg-transparent' : 'bg-brand')} />
                    <span className="min-w-0">
                      <span className={cx('block text-sm', !a.read && 'font-semibold')}>
                        {a.title}
                        {!a.read && <span className="sr-only"> (unread)</span>}
                      </span>
                      <span className="block text-xs text-muted">
                        <DateText value={a.createdAt} mode="ago" />
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-line p-1">
            <MenuItem icon={<CheckCheck />} href="/notifications">
              See all notifications
            </MenuItem>
          </div>
        </div>
      )}
    </Popover>
  );
}

function ThemeToggle() {
  const { choice } = useTheme();
  const Icon = choice === 'light' ? Sun : choice === 'dark' ? Moon : Monitor;
  return (
    <Popover
      align="right"
      label="Theme"
      trigger={({ open, toggle, id }) => (
        <IconButton label={`Theme: ${choice === 'system' ? 'match device' : choice}`} aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={toggle} className="hidden sm:grid">
          <Icon aria-hidden className="h-5 w-5" />
        </IconButton>
      )}
    >
      {(close) => <ThemeChoices close={close} />}
    </Popover>
  );
}

function ThemeChoices({ close }: { close: () => void }) {
  const { choice, setChoice } = useTheme();
  return (
    <>
      {(
        [
          ['light', 'Light', Sun],
          ['dark', 'Dark', Moon],
          ['system', 'Match device', Monitor],
        ] as const
      ).map(([v, l, I]) => (
        <MenuItem
          key={v}
          icon={<I />}
          onClick={() => {
            setChoice(v);
            close();
          }}
        >
          <span className="flex justify-between">
            {l}
            {choice === v && <Check aria-hidden className="h-4 w-4 text-brand" />}
          </span>
        </MenuItem>
      ))}
    </>
  );
}

function UserMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const initials = user.name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?';
  return (
    <Popover
      align="right"
      label="Account"
      trigger={({ open, toggle, id }) => (
        <button type="button" aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={toggle} aria-label={`Account: ${user.name}`} className="ml-1 grid h-11 w-11 place-items-center rounded-full">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-brand text-sm font-bold text-brand-ink">{initials}</span>
        </button>
      )}
    >
      {(close) => (
        <div className="w-64">
          <div className="border-b border-line px-2.5 pb-2.5 pt-2">
            <p className="truncate font-semibold">{user.name}</p>
            <p className="truncate text-xs text-muted">{user.email}</p>
            <p className="mt-1 text-xs text-muted">
              {user.roleName || 'No role'}
              {user.org && <> in {user.org.name}</>}
            </p>
          </div>
          <div className="pt-1">
            <MenuItem
              icon={<UserRound />}
              onClick={() => {
                navigate('/account');
                close();
              }}
            >
              Account and security
            </MenuItem>
            <MenuItem
              icon={<KeyRound />}
              onClick={() => {
                navigate('/account#password');
                close();
              }}
            >
              Change password
            </MenuItem>
            <div className="sm:hidden">
              <p className="px-2.5 pb-0.5 pt-2 text-xs text-muted">Theme</p>
              <ThemeChoices close={close} />
            </div>
            <MenuItem icon={<LogOut />} onClick={() => void signOut()}>
              Sign out
            </MenuItem>
          </div>
        </div>
      )}
    </Popover>
  );
}
